import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { CheckCircle2, Pause, Play, Workflow, XCircle } from 'lucide-react';
import { Typography } from 'antd';

import { StatusBadge } from './StatusBadge.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { PipelineStatus } from './types.js';

const { Text } = Typography;

export interface PipelineItem {
  id?: string;
  name: string;
  status: PipelineStatus;
}

export interface ActivePipelinesWidgetProps {
  pipelines?: readonly PipelineItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onItemClick?: (pipeline: PipelineItem) => void;
}

function getStatusIcon(status: string): typeof Workflow {
  switch (status) {
    case 'running':
      return Play;
    case 'paused':
      return Pause;
    case 'completed':
      return CheckCircle2;
    case 'failed':
      return XCircle;
    default:
      return Workflow;
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'running':
      return '#16a34a'; // amber/stone green success
    case 'failed':
      return '#dc2626'; // error red
    default:
      return '#a8a29e'; // stone-400 muted
  }
}

export function ActivePipelinesWidget({
  pipelines = [],
  className,
  testId,
  loading = false,
  onItemClick,
}: ActivePipelinesWidgetProps): ReactElement {
  const { t } = useTranslation();
  
  const widgetContainerProps = {
    title: t('crm.dashboard.pipelines.title'),
    icon: Workflow,
    iconColor: '#2563eb', // info blue
    minHeight: 180,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse p-4 flex flex-col gap-2">
          {[1, 2, 3].map((index) => (
            <div key={index} className="rounded h-8 bg-stone-200/50 w-full" />
          ))}
        </div>
      ) : pipelines.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-6 opacity-50 h-[140px]">
          <Workflow size={24} className="mb-2 text-stone-400" />
          <Text type="secondary" style={{ fontSize: 11 }}>{t('crm.dashboard.pipelines.empty')}</Text>
        </div>
      ) : (
        <div className="flex flex-col">
          {pipelines.map((pipeline) => {
            const Icon = getStatusIcon(pipeline.status);

            return (
              <button
                type="button"
                key={pipeline.id}
                onClick={onItemClick ? () => onItemClick(pipeline) : undefined}
                className="transition-colors text-left border-0 w-full"
                style={{ 
                  cursor: onItemClick ? 'pointer' : 'default',
                  opacity: onItemClick ? 1 : 0.7,
                  padding: '8px 16px',
                  borderBottom: '1px solid #f5f5f4',
                  background: 'transparent',
                }}
              >
                <div className="flex w-full items-center gap-3">
                  <Icon size={14} style={{ color: getStatusColor(pipeline.status) }} />
                  <Text strong className="truncate flex-1" style={{ fontSize: 13, color: '#44403c' }}>
                    {pipeline.name}
                  </Text>
                  <StatusBadge status={pipeline.status} size="sm" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </WidgetContainer>
  );
}
